import './ProductAccessManager.scss';
import SearchInput from '../SearchInput/SearchInput';

export type ProductLocationAccessState = 'ENABLED' | 'PREVIEW' | 'DISABLED';

export interface ProductAccessManagerProduct {
  id: string;
  name: string;
  deliveryType?: string | null;
  imageUrl?: string | null;

  access: ProductLocationAccessState;
}

export interface ProductAccessManagerProps {
  /**
   * Products grouped by their access at the
   * currently selected delivery location.
   */
  groups: {
    enabled: ProductAccessManagerProduct[];
    preview: ProductAccessManagerProduct[];
    disabled: ProductAccessManagerProduct[];
  };

  /** Current product on this load/order. */
  selectedProductId: string;

  /**
   * Product selected by the admin.
   *
   * ENABLED:
   *   simply select it.
   *
   * PREVIEW / DISABLED:
   *   host first enables it at the current
   *   DeliveryAddress, then selects it.
   */
  onPick: (productId: string, access: ProductLocationAccessState) => void;

  /**
   * Current Ship To label.
   *
   * Makes it explicit that access changes are
   * location-specific.
   */
  locationLabel?: string | null;

  search: string;

  onSearchChange: (value: string) => void;

  hasActiveSearch: boolean;

  fetching?: boolean;

  disabled?: boolean;
}

/**
 * Product selection/access manager for one
 * customer DELIVERY LOCATION.
 *
 * Product access is never changed customer-wide.
 *
 * PREVIEW and DISABLED products are enabled only
 * for the currently selected Ship To location
 * before being selected on the order.
 */
export function ProductAccessManager({
  groups,
  selectedProductId,
  onPick,
  locationLabel,
  search,
  onSearchChange,
  hasActiveSearch,
  fetching = false,
  disabled = false,
}: ProductAccessManagerProps) {
  const isEmpty =
    groups.enabled.length === 0 && groups.preview.length === 0 && groups.disabled.length === 0;

  return (
    <div className="product-access-manager">
      <div className="product-access-manager__title">Products for this delivery location</div>

      <div className="muted tiny product-access-manager__hint">
        {locationLabel ? (
          <>
            Product access below applies only to <strong>{locationLabel}</strong>. Choosing a
            preview or disabled product enables it at this location before selecting it for the
            order.
          </>
        ) : (
          <>Select a Ship To location before managing product access.</>
        )}
      </div>

      <SearchInput
        type="text"
        className="search-input product-access-manager__search"
        wrapperClassName="search-input-clearable--narrow"
        placeholder="Search products…"
        value={search}
        onValueChange={onSearchChange}
        disabled={disabled || !locationLabel}
      />

      {fetching ? (
        <div className="muted tiny">Searching…</div>
      ) : isEmpty ? (
        <div className="muted tiny">
          {hasActiveSearch ? 'No products match that search.' : 'No products found.'}
        </div>
      ) : (
        <div>
          {/* ENABLED */}
          <div>
            <div className="product-access-manager__group-label">Enabled at this location</div>

            {groups.enabled.length === 0 ? (
              <div className="muted tiny">None.</div>
            ) : (
              <div className="product-access-manager__rows">
                {groups.enabled.map((product) => (
                  <label key={product.id} className="product-access-manager__row">
                    <input
                      type="checkbox"
                      checked={product.id === selectedProductId}
                      disabled={disabled}
                      onChange={(event) => {
                        if (!event.target.checked) {
                          return;
                        }

                        onPick(product.id, 'ENABLED');
                      }}
                    />

                    <span>{product.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* PREVIEW */}
          {groups.preview.length > 0 && (
            <div className="product-access-manager__group">
              <div className="product-access-manager__group-label">Preview at this location</div>

              <div className="product-access-manager__rows">
                {groups.preview.map((product) => (
                  <label key={product.id} className="product-access-manager__row">
                    <input
                      type="checkbox"
                      checked={product.id === selectedProductId}
                      disabled={disabled}
                      // "enable & select" must fire even when this product is
                      // already selected (its box is already checked, so the
                      // click reports checked=false) — otherwise it can't enable.
                      onChange={() => onPick(product.id, 'PREVIEW')}
                    />

                    <span>
                      {product.name}

                      <span className="muted"> · preview · enable &amp; select</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* DISABLED */}
          {groups.disabled.length > 0 && (
            <div className="product-access-manager__group">
              <div className="product-access-manager__group-label">
                Not enabled at this location
              </div>

              <div className="product-access-manager__rows">
                {groups.disabled.map((product) => (
                  <label key={product.id} className="product-access-manager__row">
                    <input
                      type="checkbox"
                      checked={product.id === selectedProductId}
                      disabled={disabled}
                      // "enable & select" must fire even when this product is
                      // already selected (its box is already checked, so the
                      // click reports checked=false) — otherwise it can't enable.
                      onChange={() => onPick(product.id, 'DISABLED')}
                    />

                    <span>
                      {product.name}

                      <span className="muted"> · enable &amp; select</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProductAccessManager;
